import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Learning-system rank lexicon — 7 sushi rank names + a rank-up wrapper + a
 * recognition template (design §2/§7). New `rank` category. Display name is the
 * sushi rank; copy registers describe what the rank means musically. The
 * recognition template's `{{concept}}` is interpolated at the call site
 * (Plan 2) via the existing interpolation helper — never rendered raw.
 *
 * Bowie-authored; locked verbatim. Spec:
 * docs/superpowers/specs/2026-06-14-learning-system-with-levels-design.md
 */
export const RANK_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'rank-tinned-tuna',
    category: 'rank',
    displayName: '🥫 Tinned Tuna',
    displayNameShort: 'Tinned Tuna',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'You’re writing with a home chord — the note everything wants to come back to.',
      intermediate: 'Tonic established — your songs anchor on a clear home chord.',
      advanced: 'Tonic-level fluency — a stable key center is in place.',
    },
    tagLabel: { beginner: 'just starting', intermediate: 'tonic', advanced: 'tonic established' },
  },
  {
    id: 'rank-tuna-maki',
    category: 'rank',
    displayName: '🍣 Tuna Maki',
    displayNameShort: 'Tuna Maki',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'You’re moving between home, away, and tension chords — the basic push and pull of music.',
      intermediate: 'Diatonic function — you use tonic, predominant, and dominant chords.',
      advanced: 'Full diatonic function (T–P–D) in active use.',
    },
    tagLabel: { beginner: 'chord moves', intermediate: 'diatonic function', advanced: 'T–P–D' },
  },
  {
    id: 'rank-spicy-tuna',
    category: 'rank',
    displayName: '🌶️ Spicy Tuna',
    displayNameShort: 'Spicy Tuna',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Your phrases land with real endings, and you’re using classic chord loops.',
      intermediate: 'Cadences + named progressions — your phrases resolve and follow known patterns.',
      advanced: 'Cadential phrasing and idiomatic progressions in use.',
    },
    tagLabel: { beginner: 'real endings', intermediate: 'cadences', advanced: 'cadential phrasing' },
  },
  {
    id: 'rank-tuna-tataki',
    category: 'rank',
    displayName: '🔥 Tuna Tataki',
    displayNameShort: 'Tuna Tataki',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You’re reaching outside the plain key for extra colour.',
      intermediate: 'Modal colour + borrowed chords — you spice the key with outside chords.',
      advanced: 'Modal mixture and characteristic-note colour in active use.',
    },
    tagLabel: { beginner: 'extra colour', intermediate: 'modal colour', advanced: 'modal mixture' },
  },
  {
    id: 'rank-maguro-sashimi',
    category: 'rank',
    displayName: '🐟 Maguro Sashimi',
    displayNameShort: 'Maguro',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Your chords connect smoothly and you’re adding richer notes on top.',
      intermediate: 'Voice leading + extensions — your chords flow and carry 7ths and colour tones.',
      advanced: 'Smooth voice leading and extended harmony in use.',
    },
    tagLabel: { beginner: 'smooth + rich', intermediate: 'voice leading', advanced: 'extended harmony' },
  },
  {
    id: 'rank-toro',
    category: 'rank',
    displayName: '🥢 Toro',
    displayNameShort: 'Toro',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'You’re borrowing tension chords from other keys to pull your music around.',
      intermediate: 'Secondary dominants + reharmonization — you tonicize and re-colour freely.',
      advanced: 'Applied dominants and reharmonization techniques in active use.',
    },
    tagLabel: { beginner: 'borrowed pull', intermediate: 'secondary dominants', advanced: 'applied harmony' },
  },
  {
    id: 'rank-otoro',
    category: 'rank',
    displayName: '👑 Ōtoro',
    displayNameShort: 'Ōtoro',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'You’re changing key and shaping whole sections — the full toolkit.',
      intermediate: 'Modulation + form — you move between keys and build multi-section structures.',
      advanced: 'Key modulation and large-scale form — the complete palette.',
    },
    tagLabel: { beginner: 'the full set', intermediate: 'modulation + form', advanced: 'complete palette' },
  },
  {
    id: 'rank-up',
    category: 'rank',
    displayName: 'New rank',
    displayNameShort: 'Rank up',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Nice — your music just earned you a new rank.',
      intermediate: 'Rank up — your songs demonstrated the next tier of harmony.',
      advanced: 'Rank advanced — next concept tier mastered in your writing.',
    },
    tagLabel: { beginner: 'level up', intermediate: 'rank up', advanced: 'rank advanced' },
  },
  {
    id: 'rank-recognition',
    category: 'rank',
    displayName: 'New flavour',
    displayNameShort: 'New flavour',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'You just used {{concept}} — a new flavour on your plate.',
      intermediate: 'You just used {{concept}} — it counts toward your rank.',
      advanced: '{{concept}} detected in your writing — mastery recorded.',
    },
    tagLabel: { beginner: 'new flavour', intermediate: 'concept used', advanced: 'concept detected' },
  },
]

/**
 * Per-concept recognition labels (Bowie copy-contract answer to Joni's surfaces
 * SPEC). The engine emits machine concept keys (`function-predominant`,
 * `voice-leading`, …); the recognition nudge interpolates the label AT THE
 * USER'S BAND into `rank-recognition`'s `{{concept}}`. Grammar is fitted to the
 * three recognition frames so it always reads naturally:
 *   beginner    "You just used {{concept}} — a new flavour on your plate."   (lowercase, article baked in)
 *   intermediate"You just used {{concept}} — it counts toward your rank."     (lowercase, article baked in)
 *   advanced    "{{concept}} detected in your writing — mastery recorded."    (Capitalised, sentence-initial)
 * One entry per `LearningConcept`. Bowie-authored; locked verbatim.
 */
export const CONCEPT_LABEL_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'concept-tonic',
    category: 'rank',
    displayName: 'Tonic',
    displayNameShort: 'Tonic',
    pedagogicalLevel: 1,
    copy: { beginner: 'a home chord', intermediate: 'the tonic', advanced: 'Tonic function' },
    tagLabel: { beginner: 'home chord', intermediate: 'tonic', advanced: 'tonic' },
  },
  {
    id: 'concept-function-predominant',
    category: 'rank',
    displayName: 'Predominant',
    displayNameShort: 'Predominant',
    pedagogicalLevel: 1,
    copy: { beginner: 'a setup chord', intermediate: 'a predominant', advanced: 'Predominant function' },
    tagLabel: { beginner: 'setup chord', intermediate: 'predominant', advanced: 'predominant' },
  },
  {
    id: 'concept-function-dominant',
    category: 'rank',
    displayName: 'Dominant',
    displayNameShort: 'Dominant',
    pedagogicalLevel: 1,
    copy: { beginner: 'a tension chord', intermediate: 'a dominant', advanced: 'Dominant function' },
    tagLabel: { beginner: 'tension chord', intermediate: 'dominant', advanced: 'dominant' },
  },
  {
    id: 'concept-cadence',
    category: 'rank',
    displayName: 'Cadence',
    displayNameShort: 'Cadence',
    pedagogicalLevel: 2,
    copy: { beginner: 'a real ending', intermediate: 'a cadence', advanced: 'A cadence' },
    tagLabel: { beginner: 'an ending', intermediate: 'cadence', advanced: 'cadence' },
  },
  {
    id: 'concept-named-progression',
    category: 'rank',
    displayName: 'Named progression',
    displayNameShort: 'Progression',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'a classic chord loop',
      intermediate: 'a named progression',
      advanced: 'A named progression',
    },
    tagLabel: { beginner: 'chord loop', intermediate: 'progression', advanced: 'progression' },
  },
  {
    id: 'concept-modal-color',
    category: 'rank',
    displayName: 'Modal colour',
    displayNameShort: 'Modal colour',
    pedagogicalLevel: 2,
    copy: { beginner: 'a modal colour note', intermediate: 'modal colour', advanced: 'Modal colour' },
    tagLabel: { beginner: 'modal colour', intermediate: 'modal colour', advanced: 'modal colour' },
  },
  {
    id: 'concept-borrowed-chord',
    category: 'rank',
    displayName: 'Borrowed chord',
    displayNameShort: 'Borrowed',
    pedagogicalLevel: 2,
    copy: { beginner: 'a borrowed chord', intermediate: 'a borrowed chord', advanced: 'Modal interchange' },
    tagLabel: { beginner: 'borrowed chord', intermediate: 'borrowed chord', advanced: 'modal interchange' },
  },
  {
    id: 'concept-voice-leading',
    category: 'rank',
    displayName: 'Voice leading',
    displayNameShort: 'Voice leading',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'smooth voice leading',
      intermediate: 'smooth voice leading',
      advanced: 'Smooth voice leading',
    },
    tagLabel: { beginner: 'smooth motion', intermediate: 'voice leading', advanced: 'voice leading' },
  },
  {
    id: 'concept-extension',
    category: 'rank',
    displayName: 'Extension',
    displayNameShort: 'Extension',
    pedagogicalLevel: 3,
    copy: { beginner: 'a richer chord', intermediate: 'an extended chord', advanced: 'Extended harmony' },
    tagLabel: { beginner: 'richer chord', intermediate: 'extension', advanced: 'extension' },
  },
  {
    id: 'concept-secondary-dominant',
    category: 'rank',
    displayName: 'Secondary dominant',
    displayNameShort: 'Secondary dom',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'a borrowed tension chord',
      intermediate: 'a secondary dominant',
      advanced: 'A secondary dominant',
    },
    tagLabel: { beginner: 'borrowed tension', intermediate: 'secondary dominant', advanced: 'secondary dominant' },
  },
  {
    id: 'concept-reharmonization',
    category: 'rank',
    displayName: 'Reharmonization',
    displayNameShort: 'Reharm',
    pedagogicalLevel: 3,
    copy: { beginner: 'a chord swap', intermediate: 'a reharmonization', advanced: 'Reharmonization' },
    tagLabel: { beginner: 'chord swap', intermediate: 'reharmonization', advanced: 'reharmonization' },
  },
  {
    id: 'concept-modulation',
    category: 'rank',
    displayName: 'Modulation',
    displayNameShort: 'Modulation',
    pedagogicalLevel: 3,
    copy: { beginner: 'a key change', intermediate: 'a modulation', advanced: 'Modulation' },
    tagLabel: { beginner: 'key change', intermediate: 'modulation', advanced: 'modulation' },
  },
  {
    id: 'concept-form',
    category: 'rank',
    displayName: 'Form',
    displayNameShort: 'Form',
    pedagogicalLevel: 3,
    copy: { beginner: 'a new section', intermediate: 'a form section', advanced: 'Sectional form' },
    tagLabel: { beginner: 'a section', intermediate: 'form section', advanced: 'sectional form' },
  },
]

